/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import { Card, CardContent, CircularProgress, Divider, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Asset, AssetTransfer, AssetWithTransfer, Nullable } from "../../utils/Interfaces";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { updateMyAssets, updateMyAssetsToReceive } from "./assetTransferFunctions";
import { ArrowForward, CancelScheduleSend, Check, Close, Send } from "@mui/icons-material";
import InitiateAssetTransferModal from "./InitiateAssetTransferModal";
import { useWindowDimensions } from "../../utils/Hooks";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import CancelAssetTransferModal from "./CancelAssetTransferModal";
import AcceptAssetTransferModal from "./AcceptAssetTransferModal";

const AssetTransfers = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();

    let mobile: boolean = width <= 600;

    const [assets, setAssets] = useState<AssetWithTransfer[]>([]);
    const [assetsToReceive, setAssetsToReceive] = useState<AssetTransfer[]>([]);

    const [selectedAsset, setSelectedAsset] = useState<Nullable<AssetWithTransfer>>(null);

    const [initiateAssetTransferModalOpen, setInitiateAssetTransferModalOpen] = useState<boolean>(false);
    const [cancelAssetTransferModalOpen, setCancelAssetTransferModalOpen] = useState<boolean>(false);
    const [acceptAssetTransferModalOpen, setAcceptAssetTransferModalOpen] = useState<boolean>(false);

    const [loadingMyAssets, setLoadingMyAssets] = useState<boolean>(false);
    const [loadingMyAssetsToReceive, setLoadingMyAssetsToReceive] = useState<boolean>(false);

    useEffect(() => {
        updateMyAssets(dispatch, setAssets, setLoadingMyAssets);
        updateMyAssetsToReceive(dispatch, setAssetsToReceive, setLoadingMyAssetsToReceive);
    }, [dispatch]);

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "My Assets", route: "/assets/transfers" },
                ]}
            />

            <InitiateAssetTransferModal
                asset={selectedAsset as AssetWithTransfer}
                onAssetTransferCreated={() => updateMyAssets(dispatch, setAssets, setLoadingMyAssets)}
                open={initiateAssetTransferModalOpen}
                handleClose={() => setInitiateAssetTransferModalOpen(false)}
            />

            <CancelAssetTransferModal
                asset={selectedAsset as AssetWithTransfer}
                onAssetTransferCancelled={() => {
                    updateMyAssets(dispatch, setAssets, setLoadingMyAssets);
                    updateMyAssetsToReceive(dispatch, setAssetsToReceive, setLoadingMyAssetsToReceive);
                }}
                open={cancelAssetTransferModalOpen}
                handleClose={() => setCancelAssetTransferModalOpen(false)}
            />

            <AcceptAssetTransferModal
                asset={selectedAsset as AssetWithTransfer}
                onAssetTransferAccepted={() => {
                    updateMyAssets(dispatch, setAssets, setLoadingMyAssets);
                    updateMyAssetsToReceive(dispatch, setAssetsToReceive, setLoadingMyAssets);
                }}
                open={acceptAssetTransferModalOpen}
                handleClose={() => setAcceptAssetTransferModalOpen(false)}
            />

            {(loadingMyAssets || loadingMyAssetsToReceive) && (
                <Box width="100%" display="flex" flexDirection="row" justifyContent="center" marginTop="30%">
                    <CircularProgress size="4rem" />
                </Box>
            )}

            {assetsToReceive.length > 0 && (
                <Container sx={{ width: "100%", padding: "0px" }}>
                    <Divider>
                        <Typography variant="h6" align="center">
                            Receive Assets
                        </Typography>
                    </Divider>
                    <Box sx={{ mt: 3 }}>
                        {assetsToReceive.map((assetTransfer: AssetTransfer, key: number) => {
                            return (
                                <Card key={key} sx={{ mb: 3 }}>
                                    <CardContent>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="h6">{assetTransfer.asset.name}</Typography>
                                            <Box>
                                                <Tooltip title="Reject Asset">
                                                    <IconButton
                                                        onClick={() => {
                                                            let asset: Asset = { ...assetTransfer.asset };
                                                            (asset as AssetWithTransfer).transfer = assetTransfer;

                                                            setSelectedAsset(asset as AssetWithTransfer);
                                                            setCancelAssetTransferModalOpen(true);
                                                        }}
                                                    >
                                                        <Close color="warning" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Accept Asset">
                                                    <IconButton
                                                        onClick={() => {
                                                            let asset: Asset = { ...assetTransfer.asset };
                                                            (asset as AssetWithTransfer).transfer = assetTransfer;

                                                            setSelectedAsset(asset as AssetWithTransfer);
                                                            setAcceptAssetTransferModalOpen(true);
                                                        }}
                                                    >
                                                        <Check color="success" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>{assetTransfer.asset.manufacturer}</Typography>
                                            {!mobile && (
                                                <Typography component="span" color="info.main">
                                                    {assetTransfer.from_user.first_name}&nbsp;
                                                    {assetTransfer.from_user.last_name}&nbsp;
                                                    <ArrowForward fontSize="small" sx={{ mb: -0.65 }} color="info" />
                                                    &nbsp;
                                                    {assetTransfer.to_user.first_name}&nbsp;
                                                    {assetTransfer.to_user.last_name}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Grid direction="row" justifyContent="space-between" container>
                                            <Grid xs={mobile ? 12 : 8} item>
                                                <Typography variant="subtitle2">
                                                    P/N: <b>{assetTransfer.asset.part_number}</b>
                                                </Typography>
                                                <Typography variant="subtitle2">
                                                    S/N: <b>{assetTransfer.asset.serial_number}</b>
                                                </Typography>
                                            </Grid>
                                            {!mobile && (
                                                <Grid xs={4} item sx={{ width: "100%" }}>
                                                    <Typography variant="subtitle2" color="info.main" align="right">
                                                        Waiting for you to receive
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>

                                        {mobile && <Box sx={{ mb: 3 }} />}

                                        {mobile && (
                                            <Stack direction="row" justifyContent="flex-start">
                                                <Typography variant="body2" color="info.main">
                                                    {assetTransfer.from_user.first_name}&nbsp;
                                                    {assetTransfer.from_user.last_name}&nbsp;
                                                </Typography>
                                                <ArrowForward fontSize="small" sx={{ mb: -3 }} color="info" />
                                                <Typography variant="body2" color="info.main">
                                                    &nbsp;
                                                    {assetTransfer.to_user.first_name}&nbsp;
                                                    {assetTransfer.to_user.last_name}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {mobile && (
                                            <Typography variant="caption" color="info.main">
                                                Waiting for you to receive.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                </Container>
            )}

            {!loadingMyAssets && !loadingMyAssetsToReceive && (
                <Container sx={{ width: "100%", padding: "0px" }}>
                    <Divider>
                        <Typography variant="h6" align="center">
                            My Assets
                        </Typography>
                    </Divider>

                    <Box sx={{ mt: 3 }}>
                        {assets.length === 0 && (
                            <Typography variant="h6" align="center" marginBottom={1}>
                                You are not responsible for any Techwise assets.
                            </Typography>
                        )}

                        {assets.map((asset: AssetWithTransfer, key: number) => {
                            let transferInProgress: boolean = asset.transfer !== null;

                            return (
                                <Card key={key} sx={{ mb: 3 }}>
                                    <CardContent>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="h6">{asset.name}</Typography>
                                            <Box>
                                                {!transferInProgress && (
                                                    <Tooltip title="Transfer Asset">
                                                        <IconButton
                                                            disabled={transferInProgress}
                                                            onClick={() => {
                                                                setSelectedAsset(asset);
                                                                setInitiateAssetTransferModalOpen(true);
                                                            }}
                                                        >
                                                            <Send color="info" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {transferInProgress && (
                                                    <Tooltip title="Cancel Asset Transfer">
                                                        <IconButton
                                                            disabled={!transferInProgress}
                                                            onClick={() => {
                                                                setSelectedAsset(asset);
                                                                setCancelAssetTransferModalOpen(true);
                                                            }}
                                                        >
                                                            <CancelScheduleSend color="warning" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography>{asset.manufacturer}</Typography>
                                            {!mobile && asset.transfer !== null && (
                                                <Typography component="span" color="info.main">
                                                    {asset.transfer.from_user.first_name}&nbsp;
                                                    {asset.transfer.from_user.last_name}&nbsp;
                                                    <ArrowForward fontSize="small" sx={{ mb: -0.65 }} color="info" />
                                                    &nbsp;
                                                    {asset.transfer.to_user.first_name}&nbsp;
                                                    {asset.transfer.to_user.last_name}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Grid direction="row" justifyContent="space-between" container>
                                            <Grid xs={mobile ? 12 : 8} item>
                                                <Typography variant="subtitle2">
                                                    P/N: <b>{asset.part_number}</b>
                                                </Typography>
                                                <Typography variant="subtitle2">
                                                    S/N: <b>{asset.serial_number}</b>
                                                </Typography>
                                            </Grid>
                                            {!mobile && asset.transfer !== null && (
                                                <Grid xs={4} item>
                                                    <Typography variant="subtitle2" align="right" color="info.main">
                                                        Waiting to be received by&nbsp;
                                                        {asset.transfer.to_user.first_name}&nbsp;
                                                        {asset.transfer.to_user.last_name}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>

                                        {mobile && asset.transfer !== null && <Box sx={{ mb: 3 }} />}

                                        {mobile && asset.transfer !== null && (
                                            <Stack direction="row" justifyContent="flex-start">
                                                <Typography variant="body2" color="info.main">
                                                    {asset.transfer.from_user.first_name}&nbsp;
                                                    {asset.transfer.from_user.last_name}&nbsp;
                                                </Typography>
                                                <ArrowForward fontSize="small" sx={{ mb: -3 }} color="info" />
                                                <Typography variant="body2" color="info.main">
                                                    &nbsp;
                                                    {asset.transfer.to_user.first_name}&nbsp;
                                                    {asset.transfer.to_user.last_name}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {mobile && asset.transfer !== null && (
                                            <Typography variant="caption" color="info.main">
                                                Waiting to be received by&nbsp;
                                                {asset.transfer.to_user.first_name}&nbsp;
                                                {asset.transfer.to_user.last_name}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                </Container>
            )}
        </Box>
    );
};

export default AssetTransfers;
